import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotMovies, latestArticles, platforms } from '@/mocks/home';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const movie = hotMovies.find((m) => m.id === Number(slug) || m.slug === slug);
  const article = latestArticles.find((a) => a.slug === slug);
  const platform = platforms.find((p) => p.slug === slug);

  const item = movie || article || platform;

  if (!item) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-28 pb-20 px-4 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的内容可能已被移除或不存在</p>
            <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isMovie = 'year' in item && 'tags' in item && 'image' in item;
  const isArticle = 'excerpt' in item && 'date' in item;
  const isPlatform = 'rating' in item && 'users' in item && 'logo' in item;

  const title = (item as { title?: string; name?: string }).title || (item as { name?: string }).name || '';
  const categorySlug = (item as { category?: string }).category || 'movies';
  const categoryName = (item as { categoryName?: string }).categoryName || '';

  const relatedItems = hotMovies.filter((m) => {
    if (isMovie) return m.id !== (item as typeof hotMovies[0]).id && m.category === categorySlug;
    return m.category === categorySlug;
  }).slice(0, 4);

  return (
    <>
      <Navbar />
      <main>
        <nav className="pt-20 pb-4 px-4 md:px-6 bg-background-50" aria-label="面包屑导航">
          <div className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors">{categoryName || '分类'}</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><span className="text-foreground-700 line-clamp-1">{title}</span></li>
            </ol>
          </div>
        </nav>

        <article className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {isMovie && (
                  <span className="text-xs bg-primary-50 text-primary-600 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {(item as typeof hotMovies[0]).categoryName}
                  </span>
                )}
                {isArticle && (
                  <>
                    <span className="text-xs bg-secondary-50 text-secondary-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {(item as typeof latestArticles[0]).categoryName}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{(item as typeof latestArticles[0]).date}</span>
                  </>
                )}
                {isPlatform && (
                  <span className="flex items-center gap-1 text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500"></i>
                    {(item as typeof platforms[0]).rating} 分
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
                {title}
              </h1>

              {isMovie && (
                <div className="flex items-center gap-3 flex-wrap">
                  {(item as typeof hotMovies[0]).tags.map((tag: string) => (
                    <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap border border-background-200/60">
                      {tag}
                    </span>
                  ))}
                  <span className="text-sm text-foreground-400 whitespace-nowrap">{(item as typeof hotMovies[0]).year}年</span>
                  <span className="text-sm text-accent-500 font-semibold flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-star-fill"></i> {(item as typeof hotMovies[0]).rating} 分
                  </span>
                </div>
              )}
            </header>

            {isMovie && 'image' in item && (
              <div className="aspect-[16/9] rounded-xl overflow-hidden bg-background-200 mb-8">
                <img
                  src={(item as { image?: string }).image}
                  alt={title}
                  className="w-full h-full object-cover"
                  title={title}
                />
              </div>
            )}

            {isPlatform && 'logo' in item && (
              <div className="flex items-center gap-4 p-5 bg-background-100 rounded-xl mb-8 border border-background-200/60">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-background-200 flex-shrink-0">
                  <img
                    src={(item as { logo?: string }).logo}
                    alt={title}
                    className="w-full h-full object-cover"
                    title={title}
                  />
                </div>
                <div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-2">
                    {(item as { description?: string }).description}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-foreground-500">
                    <span><i className="ri-user-line mr-1"></i>{(item as { users?: string }).users} 用户</span>
                    <span><i className="ri-star-fill text-accent-500 mr-1"></i>{(item as { rating?: number }).rating} 分</span>
                  </div>
                </div>
              </div>
            )}

            {isArticle && 'excerpt' in item && (
              <div className="prose prose-sm max-w-none mb-8">
                <p className="text-base text-foreground-700 leading-relaxed">
                  {(item as { excerpt?: string }).excerpt}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                  茄子视频一直致力于为用户提供优质的<strong>在线看视频</strong>体验。我们精心筛选每一个推荐的<strong>视频平台</strong>，
                  确保用户能够享受到<strong>高清免费不缓冲</strong>的观影服务。无论是热门的<strong>免费电影</strong>还是最新的<strong>免费电视剧</strong>，
                  茄子视频都能为您找到最适合的<strong>视频网站</strong>。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                  我们的团队每天都会浏览大量的影视资源，为您精选出真正<strong>好用</strong>的平台。
                  像<strong>红桃视频</strong>、<strong>红杏视频</strong>这样的优质平台，经过我们的深度体验后才推荐给您，
                  确保每一部影片都能流畅播放，告别卡顿和缓冲的烦恼。
                </p>
              </div>
            )}

            {isMovie && 'description' in item && (
              <div className="prose prose-sm max-w-none mb-8">
                <h2 className="text-lg font-semibold text-foreground-950 mb-3">影片简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {(item as { description?: string }).description}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                  茄子视频为您推荐这部精彩的作品，您可以通过我们推荐的<strong>视频平台</strong>进行观看。
                  我们合作的<strong>红桃视频</strong>和<strong>红杏视频</strong>等平台都提供了<strong>高清免费不缓冲</strong>的观影体验，
                  让您尽情享受每一部<strong>免费电影</strong>和<strong>免费电视剧</strong>。
                </p>
              </div>
            )}

            <section className="border-t border-background-200/70 pt-8">
              <h2 className="text-lg font-semibold text-foreground-950 font-heading mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedItems.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-2">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        title={rel.title}
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 bg-background-950/80 backdrop-blur-sm text-accent-400 text-xs font-semibold px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        {rel.rating}
                      </div>
                    </div>
                    <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rel.title}
                    </h3>
                  </Link>
                ))}
              </div>
              {relatedItems.length === 0 && (
                <p className="text-sm text-foreground-500">暂无相关推荐</p>
              )}
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}